import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, Download, Calendar, Clock, CheckCircle, XCircle, 
  AlertTriangle, TrendingUp, ArrowLeft, Loader2, Eye, EyeOff,
  FileSpreadsheet, BarChart3, ClipboardList, Users, Hammer,
  Package, FileCheck, Wrench, PenTool, Sparkles, Printer, Edit3, Save
} from 'lucide-react';
import EventAnalyticsTab from '@/components/EventAnalyticsTab';

// Status color mapping
const getStatusColor = (status) => {
  const statusLower = (status || '').toLowerCase();
  if (statusLower === 'draft') return 'bg-slate-100 text-slate-600 border-slate-200';
  if (['submitted', 'issued', 'pending', 'pending approval', 'open', 'active', 'uploaded'].includes(statusLower)) return 'bg-blue-100 text-blue-700 border-blue-200';
  if (['approved', 'closed', 'accepted', 'completed', 'certified', 'paid'].includes(statusLower)) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (['rejected', 'declined', 'voided'].includes(statusLower)) return 'bg-red-100 text-red-700 border-red-200';
  if (statusLower.includes('comment') || statusLower.includes('revise') || statusLower.includes('review')) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const getAgingColor = (days) => {
  if (days > 14) return 'text-red-600 font-semibold';
  if (days > 7) return 'text-amber-600 font-medium';
  return 'text-slate-600';
};

// KPI Card
const KPICard = ({ label, value, icon: Icon, color = 'slate' }) => (
  <div className="bg-white border rounded-sm p-4 flex items-center gap-4" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`h-12 w-12 rounded-sm bg-${color}-100 flex items-center justify-center`}>
      <Icon className={`h-6 w-6 text-${color}-600`} />
    </div>
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
    </div>
  </div>
);

// Status Badge
const StatusBadge = ({ status }) => (
  <Badge className={`${getStatusColor(status)} text-xs font-medium`}>{status}</Badge>
);

// Data Table
const DataTable = ({ columns, data, emptyMessage = "No data available" }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{emptyMessage}</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            {columns.map((col, i) => (
              <th key={i} className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-600">{col.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, ri) => (
            <tr key={ri} className="border-b border-slate-100 hover:bg-slate-50">
              {columns.map((col, ci) => (
                <td key={ci} className="px-3 py-2">{col.render ? col.render(row) : row[col.accessor]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Module Count Card for the summary grid
const ModuleCountCard = ({ name, counts, icon: Icon, color = 'slate' }) => (
  <div className={`text-center p-3 bg-${color}-50 rounded-sm border border-${color}-100`}>
    <div className="flex items-center justify-center gap-1 mb-1">
      {Icon && <Icon className={`h-3.5 w-3.5 text-${color}-500`} />}
      <p className="text-xs text-slate-500 uppercase font-medium">{name}</p>
    </div>
    <p className="text-2xl font-bold">{counts?.total || 0}</p>
    <div className="flex justify-center gap-1 mt-1 text-xs flex-wrap">
      {counts?.draft > 0 && <Badge className="bg-slate-200 text-slate-600">{counts.draft}D</Badge>}
      {counts?.submitted > 0 && <Badge className="bg-blue-100 text-blue-700">{counts.submitted}S</Badge>}
      {counts?.approved > 0 && <Badge className="bg-emerald-100 text-emerald-700">{counts.approved}A</Badge>}
      {counts?.rejected > 0 && <Badge className="bg-red-100 text-red-700">{counts.rejected}R</Badge>}
      {counts?.pending > 0 && <Badge className="bg-amber-100 text-amber-700">{counts.pending}P</Badge>}
    </div>
  </div>
);

export default function ProjectReports() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [generating, setGenerating] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [visibilityOptions, setVisibilityOptions] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('project_inception');
  const [selectedVisibility, setSelectedVisibility] = useState('internal');
  const [reportData, setReportData] = useState(null);
  const [editedSummary, setEditedSummary] = useState('');
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // AI Report state
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiReport, setAiReport] = useState(null);
  const [aiReportEditing, setAiReportEditing] = useState(false);
  const [aiReportText, setAiReportText] = useState('');
  const [schedulesData, setSchedulesData] = useState(null);
  const [loadingSchedules, setLoadingSchedules] = useState(false);

  useEffect(() => {
    loadOptions();
    loadExistingAIReport();
  }, [projectId]);

  const loadExistingAIReport = async () => {
    try {
      const statusRes = await api.get(`/reports/ai-comprehensive/${projectId}/status`);
      const { status, result } = statusRes.data;
      if (status === 'complete' && result) {
        setAiReport(result);
        setAiReportText(result?.report_text || '');
      }
    } catch (error) {
      // No existing report - that's fine
    }
  };

  const loadOptions = async () => {
    try {
      const [periodsRes, visibilityRes] = await Promise.all([
        api.reports.getAvailablePeriods(projectId),
        api.reports.getVisibilityOptions()
      ]);
      setPeriods(periodsRes.data.presets || []);
      setVisibilityOptions(visibilityRes.data.options || []);
      const defaultOption = visibilityRes.data.options?.find(o => o.default);
      if (defaultOption) setSelectedVisibility(defaultOption.key);
    } catch (error) {
      console.error('Failed to load report options:', error);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const response = await api.reports.generate({
        project_id: projectId,
        report_type: selectedPeriod === 'last_fortnight' ? 'fortnightly' : selectedPeriod === 'project_inception' ? 'custom' : 'monthly',
        period_preset: selectedPeriod,
        visibility: selectedVisibility,
        custom_summary: editedSummary || null
      });
      setReportData(response.data);
      setEditedSummary(response.data.executive_summary || '');
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = () => {
    if (!reportData) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generatePrintHTML(reportData));
    printWindow.document.close();
    printWindow.print();
  };

  const downloadExcel = () => {
    if (!reportData) return;
    const csvData = generateCSV(reportData);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportData.project_name}_Report_${reportData.period_start}_${reportData.period_end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Excel export downloaded');
  };

  // AI Comprehensive Report with background polling
  const generateAIReport = async () => {
    setAiGenerating(true);
    try {
      // Start background generation
      await api.post(`/reports/ai-comprehensive/${projectId}`);
      
      // Poll for completion (max 120 seconds = 40 polls * 3 sec)
      for (let i = 0; i < 40; i++) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        const statusRes = await api.get(`/reports/ai-comprehensive/${projectId}/status`);
        const { status, result, error } = statusRes.data;
        
        if (status === 'complete') {
          setAiReport(result);
          setAiReportText(result?.report_text || '');
          toast.success('AI comprehensive report generated');
          return;
        }
        if (status === 'failed') {
          toast.error(error || 'AI report generation failed');
          return;
        }
      }
      toast.error('Report generation timed out. Please try again.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate AI report');
    } finally {
      setAiGenerating(false);
    }
  };

  // Print All Schedules
  const loadAndPrintAllSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const response = await api.get(`/reports/schedules/${projectId}`);
      setSchedulesData(response.data);
      // Open print window
      setTimeout(() => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(generateAllSchedulesHTML(response.data));
        printWindow.document.close();
        printWindow.print();
      }, 100);
      toast.success('Print All Schedules opened');
    } catch (error) {
      toast.error('Failed to load schedules');
    } finally {
      setLoadingSchedules(false);
    }
  };

  // Print AI report
  const printAIReport = () => {
    if (!aiReport) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generateAIReportPrintHTML(aiReport, aiReportText));
    printWindow.document.close();
    printWindow.print();
  };

  // ========== Print HTML ==========
  const generatePrintHTML = (data) => {
    const statusClass = (s) => {
      const sl = (s||'').toLowerCase();
      if (['approved','closed','completed','certified','paid'].includes(sl)) return 'status-approved';
      if (['rejected','declined','voided'].includes(sl)) return 'status-rejected';
      if (sl === 'draft') return 'status-draft';
      return 'status-submitted';
    };
    const agingClass = (d) => d > 14 ? 'aging-critical' : d > 7 ? 'aging-warning' : '';
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
    const truncate = (s, len=50) => s ? (s.length > len ? s.substring(0,len)+'...' : s) : '-';
    const fmtCurrency = (v) => v ? `R ${Number(v).toLocaleString()}` : '-';

    const renderTable = (title, headers, rows) => {
      if (!rows || rows.length === 0) return '';
      return `
        <h2>${title} (${rows.length})</h2>
        <table><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
        ${rows.join('')}</table>`;
    };

    return `<!DOCTYPE html><html><head><title>${data.project_name} - Comprehensive Project Report</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; color: #333; font-size: 12px; }
        .header { border-bottom: 3px solid #1e293b; padding-bottom: 20px; margin-bottom: 20px; }
        .header h1 { margin: 0; color: #1e293b; font-size: 24px; }
        .header p { margin: 5px 0; color: #64748b; }
        .summary { background: #f8fafc; padding: 20px; border-radius: 4px; margin-bottom: 20px; border-left: 4px solid #1e293b; }
        .kpi-strip { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
        .kpi-box { flex: 1; min-width: 100px; background: #fff; border: 1px solid #e2e8f0; padding: 12px; text-align: center; }
        .kpi-value { font-size: 22px; font-weight: bold; color: #1e293b; }
        .kpi-label { font-size: 10px; color: #64748b; text-transform: uppercase; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 11px; }
        th { background: #f1f5f9; padding: 8px; text-align: left; border: 1px solid #e2e8f0; font-size: 10px; text-transform: uppercase; }
        td { padding: 6px 8px; border: 1px solid #e2e8f0; }
        .status-approved { background: #dcfce7; color: #166534; padding: 2px 8px; border-radius: 4px; }
        .status-rejected { background: #fee2e2; color: #991b1b; padding: 2px 8px; border-radius: 4px; }
        .status-submitted { background: #dbeafe; color: #1e40af; padding: 2px 8px; border-radius: 4px; }
        .status-draft { background: #f1f5f9; color: #475569; padding: 2px 8px; border-radius: 4px; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; }
        h2 { color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-top: 30px; font-size: 16px; }
        .aging-critical { color: #dc2626; font-weight: bold; }
        .aging-warning { color: #d97706; }
        .module-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 20px; }
        .module-box { border: 1px solid #e2e8f0; padding: 10px; text-align: center; border-radius: 4px; }
        .module-box .count { font-size: 20px; font-weight: bold; }
        .module-box .label { font-size: 9px; text-transform: uppercase; color: #64748b; }
        @media print { body { padding: 20px; } }
      </style></head><body>
      <div class="header">
        <h1>${data.project_name}</h1>
        <p><strong>Report Type:</strong> ${data.report_type} | <strong>Period:</strong> ${data.period_start} to ${data.period_end}</p>
        <p><strong>Contract No:</strong> ${data.contract_no || 'N/A'} | <strong>Employer:</strong> ${data.employer || 'N/A'} | <strong>Contractor:</strong> ${data.contractor || 'N/A'}</p>
        <p><strong>Generated by:</strong> ${data.generated_by} (${data.generated_by_company || ''}) | <strong>Date:</strong> ${new Date(data.generated_at).toLocaleString()}</p>
      </div>

      <div class="summary"><h3 style="margin-top:0;">Executive Summary</h3><p>${data.executive_summary}</p></div>

      <div class="kpi-strip">
        <div class="kpi-box"><div class="kpi-value">${data.kpis.total_submissions}</div><div class="kpi-label">Submissions</div></div>
        <div class="kpi-box"><div class="kpi-value" style="color:#16a34a;">${data.kpis.total_approvals}</div><div class="kpi-label">Approvals</div></div>
        <div class="kpi-box"><div class="kpi-value" style="color:#dc2626;">${data.kpis.total_rejections}</div><div class="kpi-label">Rejections</div></div>
        <div class="kpi-box"><div class="kpi-value">${data.kpis.avg_approval_days}</div><div class="kpi-label">Avg. Days</div></div>
        <div class="kpi-box"><div class="kpi-value">${data.kpis.new_delay_events}</div><div class="kpi-label">Delay Events</div></div>
      </div>

      <h2>Module Summary</h2>
      <div class="module-grid">
        ${[
          { name: 'Site Instructions', c: data.si_counts },
          { name: 'RFIs', c: data.rfi_counts },
          { name: 'Shop Drawings', c: data.drawing_counts },
          { name: 'Delay Events', c: data.event_counts },
          { name: 'Notices', c: data.notice_counts },
          { name: 'Claims', c: data.claim_counts },
          { name: 'Variation Orders', c: data.vo_counts },
          { name: 'Meeting Minutes', c: data.meeting_counts },
          { name: 'Drawings', c: data.design_drawing_counts },
          { name: 'Procurement', c: data.procurement_counts },
          { name: 'IRS', c: data.irs_counts },
          { name: 'Subcontractors', c: data.subcontractor_counts },
          { name: 'Tasks', c: data.task_counts },
          { name: 'Site Diary', c: data.diary_counts },
          { name: 'Plant & Equip', c: data.plant_counts },
          { name: 'Milestones', c: data.milestone_counts },
          { name: 'Programmes', c: data.programme_counts }
        ].map(m => `<div class="module-box"><div class="count">${m.c?.total || 0}</div><div class="label">${m.name}</div></div>`).join('')}
      </div>

      ${renderTable('Key Actions Required', ['Ref','Module','Title','Status','Aging','Action'],
        data.key_actions?.map(a => `<tr><td>${a.ref_no}</td><td>${a.module}</td><td>${truncate(a.title,40)}</td><td><span class="${statusClass(a.status)}">${a.status}</span></td><td class="${agingClass(a.aging_days)}">${a.aging_days}d</td><td>${a.action_required}</td></tr>`)
      )}

      ${renderTable('Approvals Register', ['Ref','Module','Title','Discipline','Submitted','Status','Aging'],
        data.approvals_register?.slice(0,50).map(i => `<tr><td>${i.ref_no}</td><td>${i.module_source}</td><td>${truncate(i.title,40)}</td><td>${i.discipline||'-'}</td><td>${fmtDate(i.submitted_date)}</td><td><span class="${statusClass(i.status)}">${i.status}</span></td><td class="${agingClass(i.aging_days)}">${i.aging_days}d</td></tr>`)
      )}

      ${renderTable('Site Instructions', ['SI No','Title','Issued By','Date','Status','Aging'],
        data.site_instructions?.map(si => `<tr><td>${si.si_no}</td><td>${truncate(si.title)}</td><td>${si.issued_by||'-'}</td><td>${fmtDate(si.issue_date)}</td><td><span class="${statusClass(si.status)}">${si.status}</span></td><td class="${agingClass(si.aging_days)}">${si.aging_days}d</td></tr>`)
      )}

      ${renderTable('RFIs', ['RFI No','Subject','Sent To','Date Sent','Status','Aging'],
        data.rfis?.map(r => `<tr><td>${r.rfi_no}</td><td>${truncate(r.subject)}</td><td>${r.sent_to||'-'}</td><td>${fmtDate(r.date_sent)}</td><td><span class="${statusClass(r.status)}">${r.status}</span></td><td class="${agingClass(r.aging_days)}">${r.aging_days}d</td></tr>`)
      )}

      ${renderTable('Shop Drawings', ['Submittal','Package','Rev','Submitted','Status','Aging'],
        data.shop_drawings?.map(d => `<tr><td>${d.submittal_no}</td><td>${d.package||'-'}</td><td>${d.revision||'-'}</td><td>${fmtDate(d.submitted_date)}</td><td><span class="${statusClass(d.status)}">${d.status}</span></td><td class="${agingClass(d.aging_days)}">${d.aging_days}d</td></tr>`)
      )}

      ${renderTable('Delay Events', ['Ref','Description','Start','Status','Time Impact','Cost Impact'],
        data.delay_events?.map(e => `<tr><td>${e.event_ref}</td><td>${truncate(e.description,60)}</td><td>${fmtDate(e.start_date)}</td><td>${e.status}</td><td>${e.time_impact?'Yes':'No'}</td><td>${e.cost_impact?'Yes':'No'}</td></tr>`)
      )}

      ${renderTable('Notices', ['Ref','Title','Submitted','Status','Linked Event','Aging'],
        data.notices?.map(n => `<tr><td>${n.notice_ref}</td><td>${truncate(n.title)}</td><td>${fmtDate(n.submitted_date)}</td><td><span class="${statusClass(n.status)}">${n.status}</span></td><td>${n.linked_delay_event||'-'}</td><td class="${agingClass(n.aging_days)}">${n.aging_days}d</td></tr>`)
      )}

      ${renderTable('Claims', ['Ref','Title','Submitted','Days','Amount','Status','Aging'],
        data.claims?.map(c => `<tr><td>${c.claim_ref}</td><td>${truncate(c.title,40)}</td><td>${fmtDate(c.submitted_date)}</td><td>${c.claimed_days||'-'}</td><td>${fmtCurrency(c.claimed_amount)}</td><td><span class="${statusClass(c.status)}">${c.status}</span></td><td class="${agingClass(c.aging_days)}">${c.aging_days}d</td></tr>`)
      )}

      ${renderTable('Variation Orders', ['VO No','Title','Category','Priority','Amount','Status','Aging'],
        data.variation_orders?.map(v => `<tr><td>${v.vo_number}</td><td>${truncate(v.title,40)}</td><td>${v.change_category||'-'}</td><td>${v.priority||'-'}</td><td>${fmtCurrency(v.claimed_amount)}</td><td><span class="${statusClass(v.status)}">${v.status}</span></td><td class="${agingClass(v.aging_days)}">${v.aging_days}d</td></tr>`)
      )}

      ${renderTable('Meeting Minutes', ['Title','Type','Date','Chairperson','Status','Actions'],
        data.meeting_minutes?.map(m => `<tr><td>${truncate(m.meeting_title)}</td><td>${m.meeting_type||'-'}</td><td>${fmtDate(m.meeting_date)}</td><td>${m.chairperson||'-'}</td><td><span class="${statusClass(m.status)}">${m.status}</span></td><td>${m.action_items_count}</td></tr>`)
      )}

      ${renderTable('Drawings Register', ['Drawing No','Title','Discipline','Status','Revision','Aging'],
        data.drawings?.map(d => `<tr><td>${d.drawing_number}</td><td>${truncate(d.drawing_title)}</td><td>${d.discipline||'-'}</td><td><span class="${statusClass(d.status)}">${d.status}</span></td><td>${d.current_revision||'-'}</td><td class="${agingClass(d.aging_days)}">${d.aging_days}d</td></tr>`)
      )}

      ${renderTable('Procurement Schedule', ['Ref','Package','Subcontractor','Status','Long Lead','Critical','Delay Days'],
        data.procurement_items?.map(p => `<tr><td>${p.item_ref}</td><td>${truncate(p.package_name)}</td><td>${p.subcontractor||'-'}</td><td><span class="${statusClass(p.status)}">${p.status}</span></td><td>${p.is_long_lead?'Yes':'No'}</td><td>${p.is_critical?'Yes':'No'}</td><td>${p.delay_flag?p.delay_days+'d':'-'}</td></tr>`)
      )}

      ${renderTable('IRS Schedule', ['Activity','Discipline','Consultant','Description','Status','Date Required'],
        data.irs_items?.map(i => `<tr><td>${truncate(i.activity_name,30)}</td><td>${i.discipline||'-'}</td><td>${i.consultant||'-'}</td><td>${truncate(i.description,40)}</td><td>${i.status}</td><td>${i.date_required||'-'}</td></tr>`)
      )}

      ${renderTable('Subcontractors', ['Company','Trade','Package Value','Status','Contact','Email'],
        data.subcontractors?.map(s => `<tr><td>${s.company_name}</td><td>${s.trade||'-'}</td><td>${fmtCurrency(s.package_value)}</td><td>${s.status||'-'}</td><td>${s.contact_name||'-'}</td><td>${s.contact_email||'-'}</td></tr>`)
      )}

      <div class="footer">
        <p>This report was generated by Gantt Account Management Software on ${new Date().toLocaleString()}.</p>
        <p>CONFIDENTIAL - This document contains confidential information and is intended for the named recipient(s) only.</p>
      </div></body></html>`;
  };

  // ========== CSV Export ==========
  const generateCSV = (data) => {
    let csv = 'Approvals Register\n';
    csv += 'Ref No,Module,Title,Discipline,Submitted By,Submitted Date,Status,Aging Days\n';
    data.approvals_register?.forEach(item => {
      csv += `"${item.ref_no}","${item.module_source}","${(item.title || '').replace(/"/g, '""')}","${item.discipline || ''}","${item.submitted_by || ''}","${item.submitted_date || ''}","${item.status}","${item.aging_days}"\n`;
    });
    csv += '\nSite Instructions\n';
    csv += 'SI No,Title,Issued By,Issue Date,Status,Aging Days\n';
    data.site_instructions?.forEach(si => {
      csv += `"${si.si_no}","${(si.title || '').replace(/"/g, '""')}","${si.issued_by || ''}","${si.issue_date || ''}","${si.status}","${si.aging_days}"\n`;
    });
    csv += '\nRFIs\n';
    csv += 'RFI No,Subject,Sent To,Date Sent,Status,Aging Days\n';
    data.rfis?.forEach(r => {
      csv += `"${r.rfi_no}","${(r.subject || '').replace(/"/g, '""')}","${r.sent_to || ''}","${r.date_sent || ''}","${r.status}","${r.aging_days}"\n`;
    });
    csv += '\nDelay Events\n';
    csv += 'Event Ref,Description,Start Date,Status,Time Impact,Cost Impact\n';
    data.delay_events?.forEach(e => {
      csv += `"${e.event_ref}","${(e.description || '').replace(/"/g, '""')}","${e.start_date || ''}","${e.status}","${e.time_impact}","${e.cost_impact}"\n`;
    });
    csv += '\nVariation Orders\n';
    csv += 'VO No,Title,Category,Priority,Claimed Amount,Status,Aging Days\n';
    data.variation_orders?.forEach(v => {
      csv += `"${v.vo_number}","${(v.title || '').replace(/"/g, '""')}","${v.change_category || ''}","${v.priority || ''}","${v.claimed_amount || ''}","${v.status}","${v.aging_days}"\n`;
    });
    csv += '\nMeeting Minutes\n';
    csv += 'Title,Type,Date,Chairperson,Status,Action Items\n';
    data.meeting_minutes?.forEach(m => {
      csv += `"${(m.meeting_title || '').replace(/"/g, '""')}","${m.meeting_type || ''}","${m.meeting_date || ''}","${m.chairperson || ''}","${m.status}","${m.action_items_count}"\n`;
    });
    csv += '\nDrawings\n';
    csv += 'Drawing No,Title,Discipline,Status,Revision,Aging Days\n';
    data.drawings?.forEach(d => {
      csv += `"${d.drawing_number}","${(d.drawing_title || '').replace(/"/g, '""')}","${d.discipline || ''}","${d.status}","${d.current_revision || ''}","${d.aging_days}"\n`;
    });
    csv += '\nProcurement Items\n';
    csv += 'Ref,Package,Subcontractor,Status,Long Lead,Critical,Delay Days\n';
    data.procurement_items?.forEach(p => {
      csv += `"${p.item_ref}","${(p.package_name || '').replace(/"/g, '""')}","${p.subcontractor || ''}","${p.status}","${p.is_long_lead}","${p.is_critical}","${p.delay_days}"\n`;
    });
    csv += '\nIRS Items\n';
    csv += 'Activity,Discipline,Consultant,Description,Status,Date Required\n';
    data.irs_items?.forEach(i => {
      csv += `"${(i.activity_name || '').replace(/"/g, '""')}","${i.discipline || ''}","${i.consultant || ''}","${(i.description || '').replace(/"/g, '""')}","${i.status}","${i.date_required || ''}"\n`;
    });
    csv += '\nSubcontractors\n';
    csv += 'Company,Trade,Package Value,Status,Contact,Email\n';
    data.subcontractors?.forEach(s => {
      csv += `"${s.company_name}","${s.trade || ''}","${s.package_value || ''}","${s.status || ''}","${s.contact_name || ''}","${s.contact_email || ''}"\n`;
    });
    return csv;
  };

  // ========== Print AI Report HTML ==========
  const generateAIReportPrintHTML = (data, text) => {
    const htmlBody = text.replace(/## (.*)/g, '<h2>$1</h2>')
      .replace(/### (.*)/g, '<h3>$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n- /g, '<br/>&#8226; ')
      .replace(/\n/g, '<br/>');

    return `<!DOCTYPE html><html><head><title>${data.project_name} - Comprehensive AI Report</title>
      <style>
        body { font-family: 'Georgia', serif; padding: 50px; color: #1e293b; font-size: 13px; line-height: 1.7; max-width: 900px; margin: 0 auto; }
        .header { border-bottom: 3px solid #1e293b; padding-bottom: 20px; margin-bottom: 30px; }
        .header h1 { margin: 0; color: #1e293b; font-size: 26px; font-family: Arial, sans-serif; }
        .header .subtitle { color: #64748b; font-size: 14px; margin-top: 8px; }
        .header .meta { display: flex; gap: 24px; margin-top: 12px; font-size: 12px; color: #64748b; }
        h2 { color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-top: 35px; font-size: 18px; font-family: Arial, sans-serif; }
        h3 { color: #334155; margin-top: 20px; font-size: 15px; font-family: Arial, sans-serif; }
        p { margin: 10px 0; }
        .summary-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 20px 0; }
        .summary-card { border: 1px solid #e2e8f0; padding: 12px; text-align: center; border-radius: 4px; }
        .summary-card .count { font-size: 24px; font-weight: bold; color: #1e293b; }
        .summary-card .label { font-size: 9px; text-transform: uppercase; color: #64748b; letter-spacing: 1px; }
        .footer { margin-top: 50px; padding-top: 20px; border-top: 2px solid #e2e8f0; font-size: 10px; color: #94a3b8; }
        @media print { body { padding: 30px; } }
      </style></head><body>
      <div class="header">
        <h1>${data.project_name}</h1>
        <div class="subtitle">Comprehensive Project Status Report</div>
        <div class="meta">
          <span><strong>Contract:</strong> ${data.contract_no || 'N/A'}</span>
          <span><strong>Employer:</strong> ${data.employer || 'N/A'}</span>
          <span><strong>Contractor:</strong> ${data.contractor || 'N/A'}</span>
        </div>
        <div class="meta">
          <span><strong>Generated:</strong> ${new Date(data.generated_at).toLocaleString()}</span>
          <span><strong>By:</strong> ${data.generated_by}</span>
        </div>
      </div>
      
      <div class="summary-cards">
        ${Object.entries(data.module_summaries || {}).filter(([k]) => k !== 'programmes').map(([key, val]) => 
          `<div class="summary-card"><div class="count">${val.total}</div><div class="label">${key.replace(/_/g, ' ')}</div></div>`
        ).join('')}
      </div>

      <div>${htmlBody}</div>
      
      <div class="footer">
        <p>This report was generated by Gantt Account Management Software using AI analysis.</p>
        <p>CONFIDENTIAL - Intended for authorized recipients only. AI-generated content should be reviewed before distribution.</p>
      </div></body></html>`;
  };

  // ========== Print All Schedules HTML ==========
  const generateAllSchedulesHTML = (data) => {
    const p = data.project;
    const s = data.schedules;
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
    const fmtCurrency = (v) => v ? `R ${Number(v).toLocaleString()}` : '-';
    const statusClass = (st) => {
      const sl = (st||'').toLowerCase();
      if (['approved','closed','completed','certified'].includes(sl)) return 'status-approved';
      if (['rejected','declined'].includes(sl)) return 'status-rejected';
      if (sl === 'draft') return 'status-draft';
      return 'status-submitted';
    };

    const scheduleTable = (title, color, headers, rows) => {
      if (!rows || rows.length === 0) return `<div class="schedule-section"><h2 style="color:${color}">${title}</h2><p class="empty">No items recorded</p></div>`;
      return `<div class="schedule-section"><h2 style="color:${color}">${title} (${rows.length})</h2>
        <table><tr>${headers.map(h => `<th style="background:${color}15; color:${color}">${h}</th>`).join('')}</tr>${rows.join('')}</table></div>`;
    };

    return `<!DOCTYPE html><html><head><title>${p.name} - All Schedules</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 30px; color: #333; font-size: 11px; }
        .header { border-bottom: 3px solid #1e293b; padding-bottom: 15px; margin-bottom: 20px; }
        .header h1 { margin: 0; font-size: 22px; color: #1e293b; }
        .header p { margin: 3px 0; color: #64748b; font-size: 12px; }
        .schedule-section { page-break-inside: avoid; margin-bottom: 25px; }
        h2 { font-size: 14px; border-bottom: 2px solid; padding-bottom: 6px; margin-top: 25px; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        th { padding: 6px 8px; text-align: left; border: 1px solid #e2e8f0; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; }
        td { padding: 5px 8px; border: 1px solid #e2e8f0; font-size: 10px; }
        tr:nth-child(even) { background: #f8fafc; }
        .status-approved { background: #dcfce7; color: #166534; padding: 2px 6px; border-radius: 3px; font-size: 9px; }
        .status-rejected { background: #fee2e2; color: #991b1b; padding: 2px 6px; border-radius: 3px; font-size: 9px; }
        .status-submitted { background: #dbeafe; color: #1e40af; padding: 2px 6px; border-radius: 3px; font-size: 9px; }
        .status-draft { background: #f1f5f9; color: #475569; padding: 2px 6px; border-radius: 3px; font-size: 9px; }
        .empty { color: #94a3b8; font-style: italic; }
        .footer { margin-top: 30px; border-top: 1px solid #e2e8f0; padding-top: 10px; font-size: 9px; color: #94a3b8; }
        @media print { .schedule-section { page-break-inside: avoid; } body { padding: 15px; } }
      </style></head><body>
      <div class="header">
        <h1>${p.name} - Complete Schedules</h1>
        <p><strong>Contract:</strong> ${p.contract_no || 'N/A'} | <strong>Employer:</strong> ${p.employer || 'N/A'} | <strong>Contractor:</strong> ${p.contractor || 'N/A'}</p>
        <p><strong>Generated:</strong> ${new Date(data.generated_at).toLocaleString()}</p>
      </div>

      ${scheduleTable('Delay Events Schedule', '#ea580c', ['No.','Event No','Type','Description','Status','Duration (days)','Source','Created'],
        s.delay_events?.map((e, i) => `<tr><td>${i+1}</td><td>${e.event_number||'-'}</td><td>${e.event_type||'-'}</td><td>${(e.description||'No description').substring(0,80)}</td><td><span class="${statusClass(e.status)}">${e.status||'-'}</span></td><td>${e.total_delay_duration||'-'}</td><td>${e.source_type||'-'}</td><td>${fmtDate(e.created_at)}</td></tr>`)
      )}

      ${scheduleTable('Notices Schedule', '#2563eb', ['No.','Impact Type','Status','Linked Event','Submitted','Created'],
        s.notices?.map((n, i) => `<tr><td>${i+1}</td><td>${n.impact_type||'-'}</td><td><span class="${statusClass(n.status)}">${n.status||'-'}</span></td><td>${(n.event_id||'-').substring(0,8)}</td><td>${fmtDate(n.submitted_at)}</td><td>${fmtDate(n.created_at)}</td></tr>`)
      )}

      ${scheduleTable('Claims Schedule', '#7c3aed', ['No.','Impact Type','Status','Approved Days','Approved Amount','Linked Notice','Submitted','Created'],
        s.claims?.map((c, i) => `<tr><td>${i+1}</td><td>${c.impact_type||'-'}</td><td><span class="${statusClass(c.status)}">${c.status||'-'}</span></td><td>${c.approved_days||'-'}</td><td>${fmtCurrency(c.approved_amount)}</td><td>${(c.notice_id||'-').substring(0,8)}</td><td>${fmtDate(c.submitted_at)}</td><td>${fmtDate(c.created_at)}</td></tr>`)
      )}

      ${scheduleTable('RFI Schedule', '#0891b2', ['No.','RFI No','Title','Description','Discipline','Priority','Status','Created'],
        s.rfis?.map((r, i) => `<tr><td>${i+1}</td><td>${r.rfi_number||'-'}</td><td>${(r.title||r.subject||'').substring(0,50)}</td><td>${(r.query||r.description||'').substring(0,60) || '-'}</td><td>${r.discipline||'-'}</td><td>${r.priority||'-'}</td><td><span class="${statusClass(r.status)}">${r.status||'-'}</span></td><td>${fmtDate(r.date_created||r.created_at)}</td></tr>`)
      )}

      ${scheduleTable('Site Instructions Schedule', '#475569', ['No.','SI No','Title','Description','Discipline','Issued To/By','Status','Created'],
        s.site_instructions?.map((si, i) => `<tr><td>${i+1}</td><td>${si.si_number||'-'}</td><td>${(si.si_title||si.subject||si.title||'').substring(0,50)}</td><td>${(si.si_description||si.description||'').substring(0,60) || '-'}</td><td>${si.discipline||'-'}</td><td>${si.issued_to||si.issued_by||'-'}</td><td><span class="${statusClass(si.status)}">${si.status||'-'}</span></td><td>${fmtDate(si.created_at)}</td></tr>`)
      )}

      ${scheduleTable('Variation Orders Schedule', '#9333ea', ['No.','VO No','Title','Description','Category','Status','Created'],
        s.variation_orders?.map((v, i) => `<tr><td>${i+1}</td><td>${v.vo_number||'-'}</td><td>${(v.title||'').substring(0,50)}</td><td>${(v.description||'').substring(0,60) || '-'}</td><td>${v.change_category||'-'}</td><td><span class="${statusClass(v.status)}">${v.status||'-'}</span></td><td>${fmtDate(v.created_at)}</td></tr>`)
      )}

      ${scheduleTable('Meeting Minutes Schedule', '#0d9488', ['No.','Title','Type','Date','Location','Chairperson','Status'],
        s.meeting_minutes?.map((m, i) => `<tr><td>${i+1}</td><td>${(m.meeting_title||m.title||('Meeting '+m.meeting_number)||'').substring(0,50)}</td><td>${m.meeting_type||'-'}</td><td>${fmtDate(m.meeting_date||m.date||m.created_at)}</td><td>${m.location||m.venue||'-'}</td><td>${m.chairperson||'-'}</td><td><span class="${statusClass(m.status||'Uploaded')}">${m.status||'Uploaded'}</span></td></tr>`)
      )}

      ${scheduleTable('Shop Drawings Schedule', '#4f46e5', ['No.','Package Ref','Title','Trade','Subcontractor','Priority','Status','Created'],
        s.shop_drawings?.map((sd, i) => `<tr><td>${i+1}</td><td>${sd.package_ref||'-'}</td><td>${(sd.title||'').substring(0,50) || '-'}</td><td>${sd.trade||'-'}</td><td>${sd.subcontractor_name||'-'}</td><td>${sd.priority||'-'}</td><td><span class="${statusClass(sd.status)}">${sd.status||'-'}</span></td><td>${fmtDate(sd.created_at)}</td></tr>`)
      )}

      ${scheduleTable('Procurement Schedule', '#059669', ['No.','Package/Item','Description','Subcontractor/Supplier','Status','Long Lead','Critical','Delayed'],
        s.procurement?.map((p, i) => `<tr><td>${i+1}</td><td>${(p.package_name||p.item_name||'').substring(0,40)}</td><td>${(p.description||p.specification||'').substring(0,50) || '-'}</td><td>${p.subcontractor||p.supplier||'-'}</td><td><span class="${statusClass(p.status)}">${p.status||'-'}</span></td><td>${p.is_long_lead?'Yes':'No'}</td><td>${p.is_critical?'Yes':'No'}</td><td>${p.delay_flag?(p.delay_days+'d'):'No'}</td></tr>`)
      )}

      ${scheduleTable('IRS Schedule', '#ca8a04', ['No.','Activity','Description','Discipline','Consultant/Responsible','Status','Date Required'],
        s.irs?.map((ir, i) => `<tr><td>${i+1}</td><td>${(ir.activity_name||'').substring(0,40)}</td><td>${(ir.description||ir.information_required||'').substring(0,50) || '-'}</td><td>${ir.discipline||'-'}</td><td>${ir.consultant||ir.responsible_party||'-'}</td><td><span class="${statusClass(ir.status)}">${ir.status||'-'}</span></td><td>${fmtDate(ir.date_information_required||ir.required_date)}</td></tr>`)
      )}

      ${scheduleTable('Subcontractors Schedule', '#57534e', ['No.','Company','Trade','Scope','Status','Contact','Phone'],
        s.subcontractors?.map((sc, i) => `<tr><td>${i+1}</td><td>${sc.company_name||'-'}</td><td>${sc.trade||'-'}</td><td>${(sc.scope_description||'').substring(0,50) || '-'}</td><td><span class="${statusClass(sc.status||'Active')}">${sc.status||'Active'}</span></td><td>${sc.primary_contact?.name ? (sc.primary_contact.name + ' ' + (sc.primary_contact.surname||'')).trim() : '-'}</td><td>${sc.telephone||sc.primary_contact?.phone||'-'}</td></tr>`)
      )}

      ${scheduleTable('Drawings Register', '#0284c7', ['No.','Drawing No','Title','Discipline','Status','Current Rev','Created'],
        s.drawings?.map((d, i) => `<tr><td>${i+1}</td><td>${d.drawing_number||'-'}</td><td>${(d.drawing_title||d.title||'').substring(0,50)}</td><td>${d.discipline||'-'}</td><td><span class="${statusClass(d.status)}">${d.status||'-'}</span></td><td>${d.current_revision_code||d.current_revision||d.revision||'-'}</td><td>${fmtDate(d.created_at)}</td></tr>`)
      )}

      <div class="footer">
        <p>Generated by Gantt Account Management Software | ${new Date().toLocaleString()} | CONFIDENTIAL</p>
      </div></body></html>`;
  };

  // ========== Column Definitions ==========
  const approvalsColumns = [
    { header: 'Ref No', render: (row) => <span className="font-mono text-xs">{row.ref_no}</span> },
    { header: 'Module', render: (row) => <Badge variant="outline" className="text-xs">{row.module_source}</Badge> },
    { header: 'Title', render: (row) => <span className="text-sm">{row.title?.substring(0, 40)}{row.title?.length > 40 ? '...' : ''}</span> },
    { header: 'Discipline', render: (row) => row.discipline || '-' },
    { header: 'Submitted', render: (row) => row.submitted_date ? new Date(row.submitted_date).toLocaleDateString() : '-' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Aging', render: (row) => <span className={getAgingColor(row.aging_days)}>{row.aging_days}d</span> }
  ];

  const siColumns = [
    { header: 'SI No', render: (row) => <span className="font-mono text-xs">{row.si_no}</span> },
    { header: 'Title', render: (row) => <span className="text-sm">{row.title?.substring(0, 50)}{row.title?.length > 50 ? '...' : ''}</span> },
    { header: 'Issued By', render: (row) => row.issued_by || '-' },
    { header: 'Date', render: (row) => row.issue_date ? new Date(row.issue_date).toLocaleDateString() : '-' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Aging', render: (row) => <span className={getAgingColor(row.aging_days)}>{row.aging_days}d</span> }
  ];

  const rfiColumns = [
    { header: 'RFI No', render: (row) => <span className="font-mono text-xs">{row.rfi_no}</span> },
    { header: 'Subject', render: (row) => <span className="text-sm">{row.subject?.substring(0, 50)}{row.subject?.length > 50 ? '...' : ''}</span> },
    { header: 'Sent To', render: (row) => row.sent_to || '-' },
    { header: 'Date Sent', render: (row) => row.date_sent ? new Date(row.date_sent).toLocaleDateString() : '-' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Aging', render: (row) => <span className={getAgingColor(row.aging_days)}>{row.aging_days}d</span> }
  ];

  const eventsColumns = [
    { header: 'Ref', render: (row) => <span className="font-mono text-xs">{row.event_ref}</span> },
    { header: 'Description', render: (row) => <span className="text-sm">{row.description?.substring(0, 60)}{row.description?.length > 60 ? '...' : ''}</span> },
    { header: 'Start', render: (row) => row.start_date ? new Date(row.start_date).toLocaleDateString() : '-' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Time', render: (row) => row.time_impact ? <CheckCircle className="h-4 w-4 text-amber-500" /> : <span className="text-slate-300">-</span> },
    { header: 'Cost', render: (row) => row.cost_impact ? <CheckCircle className="h-4 w-4 text-amber-500" /> : <span className="text-slate-300">-</span> }
  ];

  const noticeColumns = [
    { header: 'Ref', render: (row) => <span className="font-mono text-xs">{row.notice_ref}</span> },
    { header: 'Title', render: (row) => <span className="text-sm">{row.title?.substring(0, 50)}{row.title?.length > 50 ? '...' : ''}</span> },
    { header: 'Submitted', render: (row) => row.submitted_date ? new Date(row.submitted_date).toLocaleDateString() : '-' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Linked Event', render: (row) => row.linked_delay_event || '-' },
    { header: 'Aging', render: (row) => <span className={getAgingColor(row.aging_days)}>{row.aging_days}d</span> }
  ];

  const claimColumns = [
    { header: 'Ref', render: (row) => <span className="font-mono text-xs">{row.claim_ref}</span> },
    { header: 'Title', render: (row) => <span className="text-sm">{row.title?.substring(0, 40)}{row.title?.length > 40 ? '...' : ''}</span> },
    { header: 'Submitted', render: (row) => row.submitted_date ? new Date(row.submitted_date).toLocaleDateString() : '-' },
    { header: 'Days', render: (row) => row.claimed_days || '-' },
    { header: 'Amount', render: (row) => row.claimed_amount ? `R ${Number(row.claimed_amount).toLocaleString()}` : '-' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Aging', render: (row) => <span className={getAgingColor(row.aging_days)}>{row.aging_days}d</span> }
  ];

  const voColumns = [
    { header: 'VO No', render: (row) => <span className="font-mono text-xs">{row.vo_number}</span> },
    { header: 'Title', render: (row) => <span className="text-sm">{row.title?.substring(0, 40)}{row.title?.length > 40 ? '...' : ''}</span> },
    { header: 'Category', render: (row) => row.change_category || '-' },
    { header: 'Priority', render: (row) => row.priority ? <Badge variant="outline" className="text-xs">{row.priority}</Badge> : '-' },
    { header: 'Amount', render: (row) => row.claimed_amount ? `R ${Number(row.claimed_amount).toLocaleString()}` : '-' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Aging', render: (row) => <span className={getAgingColor(row.aging_days)}>{row.aging_days}d</span> }
  ];

  const meetingColumns = [
    { header: 'Title', render: (row) => <span className="text-sm">{row.meeting_title?.substring(0, 40)}{row.meeting_title?.length > 40 ? '...' : ''}</span> },
    { header: 'Type', render: (row) => <Badge variant="outline" className="text-xs">{row.meeting_type || 'General'}</Badge> },
    { header: 'Date', render: (row) => row.meeting_date ? new Date(row.meeting_date).toLocaleDateString() : '-' },
    { header: 'Chairperson', render: (row) => row.chairperson || '-' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Actions', render: (row) => <span className="font-medium">{row.action_items_count || 0}</span> }
  ];

  const drawingColumns = [
    { header: 'Drawing No', render: (row) => <span className="font-mono text-xs">{row.drawing_number}</span> },
    { header: 'Title', render: (row) => <span className="text-sm">{row.drawing_title?.substring(0, 40)}{row.drawing_title?.length > 40 ? '...' : ''}</span> },
    { header: 'Discipline', render: (row) => row.discipline || '-' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Revision', render: (row) => row.current_revision || '-' },
    { header: 'Aging', render: (row) => <span className={getAgingColor(row.aging_days)}>{row.aging_days}d</span> }
  ];

  const procurementColumns = [
    { header: 'Ref', render: (row) => <span className="font-mono text-xs">{row.item_ref}</span> },
    { header: 'Package', render: (row) => <span className="text-sm">{row.package_name?.substring(0, 30)}{row.package_name?.length > 30 ? '...' : ''}</span> },
    { header: 'Subcontractor', render: (row) => row.subcontractor || '-' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Long Lead', render: (row) => row.is_long_lead ? <CheckCircle className="h-4 w-4 text-amber-500" /> : <span className="text-slate-300">-</span> },
    { header: 'Critical', render: (row) => row.is_critical ? <AlertTriangle className="h-4 w-4 text-red-500" /> : <span className="text-slate-300">-</span> },
    { header: 'Delay', render: (row) => row.delay_flag ? <span className="text-red-600 font-semibold">{row.delay_days}d</span> : '-' }
  ];

  const irsColumns = [
    { header: 'Activity', render: (row) => <span className="text-sm">{row.activity_name?.substring(0, 30)}{row.activity_name?.length > 30 ? '...' : ''}</span> },
    { header: 'Discipline', render: (row) => row.discipline || '-' },
    { header: 'Consultant', render: (row) => row.consultant || '-' },
    { header: 'Description', render: (row) => <span className="text-sm">{row.description?.substring(0, 40)}{row.description?.length > 40 ? '...' : ''}</span> },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Required', render: (row) => row.date_required || '-' }
  ];

  const subcontractorColumns = [
    { header: 'Company', render: (row) => <span className="font-medium text-sm">{row.company_name}</span> },
    { header: 'Trade', render: (row) => row.trade || '-' },
    { header: 'Package Value', render: (row) => row.package_value ? `R ${Number(row.package_value).toLocaleString()}` : '-' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status || 'Active'} /> },
    { header: 'Contact', render: (row) => row.contact_name || '-' },
    { header: 'Email', render: (row) => row.contact_email || '-' }
  ];

  const keyActionsColumns = [
    { header: 'Ref', render: (row) => <span className="font-mono text-xs">{row.ref_no}</span> },
    { header: 'Module', render: (row) => <Badge variant="outline" className="text-xs">{row.module}</Badge> },
    { header: 'Title', render: (row) => <span className="text-sm">{row.title?.substring(0, 40)}</span> },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Aging', render: (row) => <span className={`${getAgingColor(row.aging_days)} font-bold`}>{row.aging_days}d</span> },
    { header: 'Action Required', render: (row) => <span className="text-xs text-red-600 font-medium">{row.action_required}</span> }
  ];

  // ========== Module Sections Config ==========
  const allModules = [
    { name: 'Site Instructions', counts: reportData?.si_counts, icon: FileText, color: 'blue' },
    { name: 'RFIs', counts: reportData?.rfi_counts, icon: FileCheck, color: 'indigo' },
    { name: 'Shop Drawings', counts: reportData?.drawing_counts, icon: PenTool, color: 'violet' },
    { name: 'Delay Events', counts: reportData?.event_counts, icon: AlertTriangle, color: 'amber' },
    { name: 'Notices', counts: reportData?.notice_counts, icon: FileText, color: 'orange' },
    { name: 'Claims', counts: reportData?.claim_counts, icon: ClipboardList, color: 'red' },
    { name: 'Variation Orders', counts: reportData?.vo_counts, icon: Wrench, color: 'purple' },
    { name: 'Meeting Minutes', counts: reportData?.meeting_counts, icon: Users, color: 'teal' },
    { name: 'Drawings', counts: reportData?.design_drawing_counts, icon: PenTool, color: 'cyan' },
    { name: 'Procurement', counts: reportData?.procurement_counts, icon: Package, color: 'emerald' },
    { name: 'IRS', counts: reportData?.irs_counts, icon: Hammer, color: 'yellow' },
    { name: 'Subcontractors', counts: reportData?.subcontractor_counts, icon: Users, color: 'slate' },
    { name: 'Tasks', counts: reportData?.task_counts, icon: ClipboardList, color: 'sky' },
    { name: 'Site Diary', counts: reportData?.diary_counts, icon: FileText, color: 'lime' },
    { name: 'Plant & Equipment', counts: reportData?.plant_counts, icon: Wrench, color: 'stone' },
    { name: 'Milestones', counts: reportData?.milestone_counts, icon: Calendar, color: 'rose' },
    { name: 'Programmes', counts: reportData?.programme_counts, icon: BarChart3, color: 'fuchsia' }
  ];

  return (
    <DashboardLayout>
    <div className="space-y-4 sm:space-y-6 overflow-x-hidden" data-testid="project-reports-page">
      {/* Header */}
      <div className="flex items-start sm:items-center gap-2 sm:gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="p-2 shrink-0" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0">
          <h1 className="text-base sm:text-2xl font-barlow font-bold uppercase tracking-tight">Comprehensive Project Report</h1>
          <p className="text-xs sm:text-sm text-slate-500">Generate reports covering all 17 project modules</p>
        </div>
      </div>

      {/* Top-level Tabs: Data Report vs AI Report */}
      <Tabs defaultValue="data-report" className="space-y-4">
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="bg-white border w-full sm:w-auto" data-testid="top-tabs">
            <TabsTrigger value="data-report" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-4">
              <BarChart3 className="h-4 w-4 sm:mr-2 shrink-0" /><span className="hidden sm:inline">Data Report</span>
            </TabsTrigger>
            <TabsTrigger value="ai-report" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-4">
              <Sparkles className="h-4 w-4 sm:mr-2 shrink-0" /><span className="hidden sm:inline">AI Written Report</span>
            </TabsTrigger>
            <TabsTrigger value="print-all" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-4">
              <Printer className="h-4 w-4 sm:mr-2 shrink-0" /><span className="hidden sm:inline">Print All Schedules</span>
            </TabsTrigger>
            <TabsTrigger value="event-analytics" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-4" data-testid="event-analytics-tab">
              <AlertTriangle className="h-4 w-4 sm:mr-2 shrink-0" /><span className="hidden sm:inline">Event Analytics</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* ========== DATA REPORT TAB ========== */}
        <TabsContent value="data-report" className="space-y-6">

      {/* Controls */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                <Calendar className="h-3 w-3 inline mr-1" />Report Period
              </Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="rounded-sm" data-testid="period-select">
                  <SelectValue placeholder="Select period..." />
                </SelectTrigger>
                <SelectContent>
                  {periods.map(p => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.label} <span className="text-xs text-slate-400 ml-2">({p.description})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                <Eye className="h-3 w-3 inline mr-1" />Report Type
              </Label>
              <Select value={selectedVisibility} onValueChange={setSelectedVisibility}>
                <SelectTrigger className="rounded-sm" data-testid="visibility-select">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {visibilityOptions.map(v => (
                    <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedVisibility === 'external' && (
                <p className="text-xs text-amber-600 mt-1"><EyeOff className="h-3 w-3 inline mr-1" />Draft items will be hidden</p>
              )}
            </div>
            <div className="flex items-end">
              <Button onClick={generateReport} disabled={generating} className="w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="generate-report-btn">
                {generating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>) : (<><BarChart3 className="h-4 w-4 mr-2" />Generate Report</>)}
              </Button>
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={downloadPDF} disabled={!reportData} className="flex-1" data-testid="download-pdf-btn">
                <Download className="h-4 w-4 mr-2" />PDF
              </Button>
              {selectedVisibility === 'internal' && (
                <Button variant="outline" onClick={downloadExcel} disabled={!reportData} className="flex-1" data-testid="download-excel-btn">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />Excel
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      {reportData && (
        <>
          {/* Header Info */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                <div className="min-w-0">
                  <h2 className="text-base sm:text-xl font-barlow font-bold uppercase" data-testid="report-project-name">{reportData.project_name}</h2>
                  <p className="text-xs sm:text-sm text-slate-500 mt-1">{reportData.report_type} Report | {reportData.period_start} to {reportData.period_end}</p>
                  <div className="flex flex-wrap gap-2 sm:gap-4 mt-2 text-xs text-slate-500">
                    <span><strong>Contract:</strong> {reportData.contract_no || 'N/A'}</span>
                    <span><strong>Employer:</strong> {reportData.employer || 'N/A'}</span>
                    <span><strong>Visibility:</strong> <Badge variant="outline" className="text-xs">{reportData.visibility}</Badge></span>
                  </div>
                </div>
                <div className="text-left sm:text-right text-xs text-slate-500 shrink-0">
                  <p>Generated by: {reportData.generated_by}</p>
                  <p>{new Date(reportData.generated_at).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Executive Summary */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-sm font-bold uppercase tracking-wider">Executive Summary</CardTitle>
                <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-xs" data-testid="edit-summary-btn">Edit Summary</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Edit Executive Summary</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <Textarea value={editedSummary} onChange={(e) => setEditedSummary(e.target.value)} rows={8} className="w-full" />
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setSummaryDialogOpen(false)}>Cancel</Button>
                        <Button onClick={() => { setSummaryDialogOpen(false); generateReport(); }}>Regenerate with Changes</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700 leading-relaxed" data-testid="executive-summary">{reportData.executive_summary}</p>
            </CardContent>
          </Card>

          {/* KPI Strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KPICard label="Total Submissions" value={reportData.kpis.total_submissions} icon={TrendingUp} color="blue" />
            <KPICard label="Approvals" value={reportData.kpis.total_approvals} icon={CheckCircle} color="emerald" />
            <KPICard label="Rejections" value={reportData.kpis.total_rejections} icon={XCircle} color="red" />
            <KPICard label="Avg. Approval Days" value={reportData.kpis.avg_approval_days} icon={Clock} color="slate" />
            <KPICard label="Delay Events" value={reportData.kpis.new_delay_events} icon={AlertTriangle} color="amber" />
          </div>

          {/* Module Counts - All 17 modules */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold uppercase tracking-wider">Module Summary (All 17 Modules)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="module-summary-grid">
                {allModules.map((m, i) => (
                  <ModuleCountCard key={i} name={m.name} counts={m.counts} icon={m.icon} color={m.color} />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="overflow-x-auto">
              <TabsList className="inline-flex w-auto min-w-full" data-testid="report-tabs">
                <TabsTrigger value="overview">Key Actions</TabsTrigger>
                <TabsTrigger value="approvals">Approvals</TabsTrigger>
                <TabsTrigger value="si">SIs ({reportData.site_instructions?.length || 0})</TabsTrigger>
                <TabsTrigger value="rfis">RFIs ({reportData.rfis?.length || 0})</TabsTrigger>
                <TabsTrigger value="shop-drawings">Shop Dwgs ({reportData.shop_drawings?.length || 0})</TabsTrigger>
                <TabsTrigger value="events">Events ({reportData.delay_events?.length || 0})</TabsTrigger>
                <TabsTrigger value="notices">Notices ({reportData.notices?.length || 0})</TabsTrigger>
                <TabsTrigger value="claims">Claims ({reportData.claims?.length || 0})</TabsTrigger>
                <TabsTrigger value="vos">VOs ({reportData.variation_orders?.length || 0})</TabsTrigger>
                <TabsTrigger value="meetings">Meetings ({reportData.meeting_minutes?.length || 0})</TabsTrigger>
                <TabsTrigger value="drawings">Drawings ({reportData.drawings?.length || 0})</TabsTrigger>
                <TabsTrigger value="procurement">Procurement ({reportData.procurement_items?.length || 0})</TabsTrigger>
                <TabsTrigger value="irs">IRS ({reportData.irs_items?.length || 0})</TabsTrigger>
                <TabsTrigger value="subcontractors">Subs ({reportData.subcontractors?.length || 0})</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-500" />Key Actions Required</CardTitle></CardHeader>
                <CardContent><DataTable columns={keyActionsColumns} data={reportData.key_actions} emptyMessage="No urgent actions required - all items are on track!" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="approvals">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">Approvals Register ({reportData.approvals_register?.length || 0})</CardTitle></CardHeader>
                <CardContent><DataTable columns={approvalsColumns} data={reportData.approvals_register} emptyMessage="No approvals in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="si">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">Site Instructions</CardTitle></CardHeader>
                <CardContent><DataTable columns={siColumns} data={reportData.site_instructions} emptyMessage="No Site Instructions in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="rfis">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">RFIs</CardTitle></CardHeader>
                <CardContent><DataTable columns={rfiColumns} data={reportData.rfis} emptyMessage="No RFIs in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="shop-drawings">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">Shop Drawings</CardTitle></CardHeader>
                <CardContent><DataTable columns={[
                  { header: 'Submittal', render: (row) => <span className="font-mono text-xs">{row.submittal_no}</span> },
                  { header: 'Package', render: (row) => row.package || '-' },
                  { header: 'Rev', render: (row) => row.revision || '-' },
                  { header: 'Submitted', render: (row) => row.submitted_date ? new Date(row.submitted_date).toLocaleDateString() : '-' },
                  { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
                  { header: 'Aging', render: (row) => <span className={getAgingColor(row.aging_days)}>{row.aging_days}d</span> }
                ]} data={reportData.shop_drawings} emptyMessage="No Shop Drawings in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="events">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">Delay Events</CardTitle></CardHeader>
                <CardContent><DataTable columns={eventsColumns} data={reportData.delay_events} emptyMessage="No Delay Events in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notices">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">Notices</CardTitle></CardHeader>
                <CardContent><DataTable columns={noticeColumns} data={reportData.notices} emptyMessage="No Notices in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="claims">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">Claims</CardTitle></CardHeader>
                <CardContent><DataTable columns={claimColumns} data={reportData.claims} emptyMessage="No Claims in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="vos">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">Variation Orders</CardTitle></CardHeader>
                <CardContent><DataTable columns={voColumns} data={reportData.variation_orders} emptyMessage="No Variation Orders in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="meetings">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">Meeting Minutes</CardTitle></CardHeader>
                <CardContent><DataTable columns={meetingColumns} data={reportData.meeting_minutes} emptyMessage="No Meeting Minutes in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="drawings">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">Drawings Register</CardTitle></CardHeader>
                <CardContent><DataTable columns={drawingColumns} data={reportData.drawings} emptyMessage="No Drawings in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="procurement">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">Procurement Schedule</CardTitle></CardHeader>
                <CardContent><DataTable columns={procurementColumns} data={reportData.procurement_items} emptyMessage="No Procurement Items in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="irs">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">IRS Schedule</CardTitle></CardHeader>
                <CardContent><DataTable columns={irsColumns} data={reportData.irs_items} emptyMessage="No IRS Items in this period" /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="subcontractors">
              <Card><CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider">Subcontractors</CardTitle></CardHeader>
                <CardContent><DataTable columns={subcontractorColumns} data={reportData.subcontractors} emptyMessage="No Subcontractors on this project" /></CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Visibility Note */}
          {reportData.filtered_due_to_visibility > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
              <p className="text-sm text-amber-800">
                <EyeOff className="h-4 w-4 inline mr-2" />
                <strong>Note:</strong> {reportData.filtered_due_to_visibility} draft item(s) were excluded from this report due to the selected visibility settings.
              </p>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!reportData && !generating && (
        <Card>
          <CardContent className="py-16 text-center">
            <FileText className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No Report Generated</h3>
            <p className="text-sm text-slate-500 mb-4">
              Select your report options above and click "Generate Report" to create a comprehensive project report covering all modules.
            </p>
          </CardContent>
        </Card>
      )}

        </TabsContent>

        {/* ========== AI WRITTEN REPORT TAB ========== */}
        <TabsContent value="ai-report" className="space-y-6">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div className="min-w-0">
                  <h2 className="text-base sm:text-lg font-barlow font-bold uppercase tracking-tight">AI Comprehensive Written Report</h2>
                  <p className="text-xs sm:text-sm text-slate-500">Generate a detailed narrative report analyzing all 17 project modules using AI</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={generateAIReport} 
                    disabled={aiGenerating}
                    className="bg-slate-900 text-white hover:bg-slate-800"
                    data-testid="generate-ai-report-btn"
                  >
                    {aiGenerating ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" />Generate AI Report</>
                    )}
                  </Button>
                  {aiReport && (
                    <Button variant="outline" onClick={printAIReport} data-testid="print-ai-report-btn">
                      <Printer className="h-4 w-4 mr-2" />Print
                    </Button>
                  )}
                </div>
              </div>
              {aiGenerating && (
                <div className="text-center py-12">
                  <Loader2 className="h-10 w-10 animate-spin text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-500">AI is analyzing all 17 project modules and generating a comprehensive written report...</p>
                  <p className="text-xs text-slate-400 mt-2">This may take 30-60 seconds</p>
                </div>
              )}
            </CardContent>
          </Card>

          {aiReport && !aiGenerating && (
            <>
              {/* Module Summary Cards */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider">Module Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="ai-module-summary">
                    {[
                      { key: 'delay_events', label: 'Delay Events', color: 'orange' },
                      { key: 'notices', label: 'Notices', color: 'blue' },
                      { key: 'claims', label: 'Claims', color: 'purple' },
                      { key: 'rfis', label: 'RFIs', color: 'cyan' },
                      { key: 'site_instructions', label: 'SIs', color: 'slate' },
                      { key: 'variation_orders', label: 'VOs', color: 'violet' },
                      { key: 'meeting_minutes', label: 'Meetings', color: 'teal' },
                      { key: 'shop_drawings', label: 'Shop Dwgs', color: 'indigo' },
                      { key: 'procurement', label: 'Procurement', color: 'emerald' },
                      { key: 'irs', label: 'IRS', color: 'yellow' },
                      { key: 'subcontractors', label: 'Subcons', color: 'stone' },
                      { key: 'drawings', label: 'Drawings', color: 'sky' },
                      { key: 'tasks', label: 'Tasks', color: 'rose' },
                      { key: 'site_diary', label: 'Site Diary', color: 'lime' },
                      { key: 'plant_equipment', label: 'Plant', color: 'amber' },
                      { key: 'milestones', label: 'Milestones', color: 'fuchsia' },
                      { key: 'programmes', label: 'Programmes', color: 'red' },
                    ].map(mod => {
                      const summary = aiReport.module_summaries?.[mod.key] || {};
                      return (
                        <div key={mod.key} className={`text-center p-3 bg-${mod.color}-50 rounded-sm border border-${mod.color}-100`}>
                          <p className="text-2xl font-bold">{summary.total || 0}</p>
                          <p className="text-[10px] text-slate-500 uppercase font-medium">{mod.label}</p>
                          {summary.statuses && Object.keys(summary.statuses).length > 0 && (
                            <div className="flex justify-center gap-1 mt-1 flex-wrap">
                              {Object.entries(summary.statuses).slice(0, 3).map(([status, count]) => (
                                <Badge key={status} className={`${getStatusColor(status)} text-[9px]`}>{count}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Financial highlights */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    {aiReport.module_summaries?.claims?.total_approved_amount > 0 && (
                      <div className="bg-purple-50 border border-purple-200 rounded-sm p-3">
                        <p className="text-xs text-purple-600 uppercase font-bold">Claims Approved</p>
                        <p className="text-lg font-bold">R {Number(aiReport.module_summaries.claims.total_approved_amount).toLocaleString()}</p>
                        <p className="text-xs text-purple-500">{aiReport.module_summaries.claims.total_approved_days} days approved</p>
                      </div>
                    )}
                    {aiReport.module_summaries?.variation_orders?.total_claimed > 0 && (
                      <div className="bg-violet-50 border border-violet-200 rounded-sm p-3">
                        <p className="text-xs text-violet-600 uppercase font-bold">VOs Claimed</p>
                        <p className="text-lg font-bold">R {Number(aiReport.module_summaries.variation_orders.total_claimed).toLocaleString()}</p>
                        <p className="text-xs text-violet-500">R {Number(aiReport.module_summaries.variation_orders.total_approved).toLocaleString()} approved</p>
                      </div>
                    )}
                    {aiReport.module_summaries?.procurement?.delayed > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                        <p className="text-xs text-red-600 uppercase font-bold">Procurement Alerts</p>
                        <p className="text-lg font-bold">{aiReport.module_summaries.procurement.delayed} Delayed</p>
                        <p className="text-xs text-red-500">{aiReport.module_summaries.procurement.critical} critical, {aiReport.module_summaries.procurement.long_lead} long-lead</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Written Report */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-bold uppercase tracking-wider">Written Report</CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setAiReportEditing(!aiReportEditing)}
                      data-testid="edit-ai-report-btn"
                    >
                      {aiReportEditing ? <><Save className="h-4 w-4 mr-1" />Done</> : <><Edit3 className="h-4 w-4 mr-1" />Edit</>}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {aiReportEditing ? (
                    <Textarea
                      value={aiReportText}
                      onChange={(e) => setAiReportText(e.target.value)}
                      rows={30}
                      className="font-mono text-sm w-full"
                      data-testid="ai-report-editor"
                    />
                  ) : (
                    <div className="prose prose-sm max-w-none prose-headings:font-barlow prose-headings:uppercase prose-h2:text-base prose-h2:border-b prose-h2:border-slate-200 prose-h2:pb-2 prose-h2:mt-8 prose-h3:text-sm" data-testid="ai-report-content">
                      {aiReportText.split('\n').map((line, i) => {
                        if (line.startsWith('## ')) return <h2 key={i} className="text-base font-barlow font-bold uppercase border-b border-slate-200 pb-2 mt-8 mb-3">{line.replace('## ', '')}</h2>;
                        if (line.startsWith('### ')) return <h3 key={i} className="text-sm font-bold mt-5 mb-2">{line.replace('### ', '')}</h3>;
                        if (line.startsWith('- ')) return <li key={i} className="ml-4 text-sm text-slate-700">{line.replace('- ', '').replace(/\*\*(.*?)\*\*/g, (_, t) => t)}</li>;
                        if (line.trim() === '') return <br key={i} />;
                        return <p key={i} className="text-sm text-slate-700 leading-relaxed mb-2">{line.replace(/\*\*(.*?)\*\*/g, (_, t) => t)}</p>;
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {!aiReport && !aiGenerating && (
            <Card>
              <CardContent className="py-16 text-center">
                <Sparkles className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No AI Report Generated</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Click "Generate AI Report" to create a comprehensive written project status report using AI analysis of all 17 modules.
                </p>
                <Button onClick={generateAIReport} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="generate-ai-report-cta">
                  <Sparkles className="h-4 w-4 mr-2" />Generate AI Report
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========== PRINT ALL SCHEDULES TAB ========== */}
        <TabsContent value="print-all" className="space-y-6">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div className="min-w-0">
                  <h2 className="text-base sm:text-lg font-barlow font-bold uppercase tracking-tight">Print All Schedules</h2>
                  <p className="text-xs sm:text-sm text-slate-500">Print a combined document containing all 17 module schedules in one go</p>
                </div>
                <Button 
                  onClick={loadAndPrintAllSchedules} 
                  disabled={loadingSchedules}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="print-all-schedules-btn"
                >
                  {loadingSchedules ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading...</>
                  ) : (
                    <><Printer className="h-4 w-4 mr-2" />Print All Schedules</>
                  )}
                </Button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                {[
                  { label: 'Delay Events', icon: AlertTriangle, color: 'orange' },
                  { label: 'Notices', icon: FileText, color: 'blue' },
                  { label: 'Claims', icon: ClipboardList, color: 'purple' },
                  { label: 'RFIs', icon: FileCheck, color: 'cyan' },
                  { label: 'Site Instructions', icon: FileText, color: 'slate' },
                  { label: 'Variation Orders', icon: Wrench, color: 'violet' },
                  { label: 'Meeting Minutes', icon: Users, color: 'teal' },
                  { label: 'Shop Drawings', icon: PenTool, color: 'indigo' },
                  { label: 'Procurement', icon: Package, color: 'emerald' },
                  { label: 'IRS', icon: Hammer, color: 'yellow' },
                  { label: 'Subcontractors', icon: Users, color: 'stone' },
                  { label: 'Drawings', icon: PenTool, color: 'sky' },
                  { label: 'Tasks', icon: ClipboardList, color: 'rose' },
                  { label: 'Site Diary', icon: FileText, color: 'lime' },
                  { label: 'Plant & Equipment', icon: Wrench, color: 'amber' },
                  { label: 'Milestones', icon: Calendar, color: 'fuchsia' },
                  { label: 'Programmes', icon: BarChart3, color: 'red' },
                ].map((mod) => (
                  <div key={mod.label} className={`flex items-center gap-1.5 sm:gap-2 p-2 sm:p-3 bg-${mod.color}-50 border border-${mod.color}-100 rounded-sm`}>
                    <mod.icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 text-${mod.color}-500 shrink-0`} />
                    <span className="text-[10px] sm:text-xs font-medium truncate">{mod.label}</span>
                    <CheckCircle className="h-3 w-3 text-emerald-500 ml-auto shrink-0" />
                  </div>
                ))}
              </div>

              <p className="text-xs text-slate-400 mt-4">
                All 17 schedules will be compiled into a single printable document with colour-coded headers for each module. Individual module schedules can be printed separately from their respective tabs.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Event Analytics Tab */}
        <TabsContent value="event-analytics" data-testid="event-analytics-content">
          <EventAnalyticsTab projectId={projectId} />
        </TabsContent>

      </Tabs>
    </div>
    </DashboardLayout>
  );
}
